import {
  EntityClassProperty,
  formatProperty,
  formatPropertyBasedOnNameValue,
  IndicatorChip,
  JupiterTheme,
  parseAsJson,
  PropertyRevisionData,
} from '@jupiterone/jupiter-web-toolkit';
import { IconButton, Menu, Tooltip, Typography } from '@material-ui/core';
import MenuItem, { MenuItemProps } from '@material-ui/core/MenuItem';
import { createStyles, withStyles, WithStyles } from '@material-ui/styles';
import React, { ReactElement } from 'react';
import { MoreHorizontal as MoreIcon, Tag as TagIcon } from 'react-feather';
import { useAnchorElState } from 'react-use-object-state';

type UpdateEntityFunction = (
  timestamp: number,
  properties: any
) => Promise<void>;

const styles = (theme: JupiterTheme) =>
  createStyles({
    row: {
      display: 'grid',
      gridTemplateColumns: 'auto 73px',
    },
    chip: {
      fontSize: 11,
      height: 20,
      width: 80,
      margin: theme.spacing(0, 1),
    },
    chipContainer: {
      display: 'inline-block',
    },
    propertyName: {
      color: theme.palette.navy[400],
    },
    propertyValue: {
      gridColumn: 'span 2',
      lineHeight: 1.35,
      marginBottom: theme.spacing(2),
      marginRight: theme.spacing(3),
      wordBreak: 'break-all',
      '& pre': {
        background: theme.palette.coolGrey[50],
        borderRadius: 3,
        fontFamily: `'Ubuntu Mono', monospace`,
        fontSize: theme.typography.caption.fontSize,
        margin: 0,
        width: 351,
        overflowX: 'auto',
        padding: theme.spacing(0.5),
      },
    },
    listItem: {
      borderTop: 'solid 1px ' + theme.palette.coolGrey[100],
      padding: theme.spacing(0.5, 0),
      '&:last-child': {
        borderBottom: 'solid 1px ' + theme.palette.coolGrey[100],
      },
      '&:only-child': {
        border: 'none',
      },
    },
    tagContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    tag: {
      color: theme.palette.navy[400],
      width: '14px',
      marginRight: theme.spacing(0.375),
      position: 'relative',
      top: 1,
    },
    webLinkButton: {
      color: theme.palette.primary.main,
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      letterSpacing: 'normal',
      marginLeft: -theme.spacing(1),
      marginRight: -theme.spacing(1),
      textTransform: 'none',
    },
    webLinkText: {
      '& a, & div': {
        color: theme.palette.primary.main,
        fontSize: theme.typography.body1.fontSize,
        lineHeight: 1.3,
        letterSpacing: 'normal',
        textTransform: 'none',
        textAlign: 'left',
      },
    },
    moreButton: {
      color: theme.palette.coolGrey[300],
      padding: theme.spacing(0, 2, 0.25),
      '&:hover, &:focus': {
        color: theme.palette.primary.main,
      },
    },
  });

interface Props extends WithStyles<typeof styles> {
  updateEntity?: UpdateEntityFunction;
  propertyName: string;
  propertyData: PropertyRevisionData;
  entityType: string;
  propertyDefinition?: EntityClassProperty;
  propertyActions?: ReactElement<MenuItemProps>[];
}
const TAG_KEY = 'tag.';

export const EntityDrawerProperty = ({
  updateEntity,
  classes,
  propertyName,
  propertyData,
  propertyActions = [],
  propertyDefinition,
  entityType,
}: Props) => {
  const menu = useAnchorElState(null);

  const getFieldContent = (propertyName: string, propertyValue: any) => {
    if (Array.isArray(propertyValue)) {
      return propertyValue.map((propertyValueItem, index) => (
        // recursively get field content for each item in array of property values
        <div className={classes.listItem} key={index}>
          {getFieldContent(propertyName, propertyValueItem)}
        </div>
      ));
    }

    const propertyValueParsedJson = parseAsJson(propertyValue);
    if (propertyValueParsedJson) {
      return <pre>{JSON.stringify(propertyValueParsedJson, null, 2)}</pre>;
    }

    if (
      typeof propertyValue === 'string' &&
      propertyValue.match(/^#.*$/gm) &&
      propertyValue.includes('\n')
    ) {
      // this is probably Markdown
      return <pre>{propertyValue}</pre>;
    }

    if (propertyDefinition && propertyDefinition.format) {
      return formatProperty(propertyDefinition.format, propertyValue, classes);
    }

    if (!propertyDefinition) {
      return formatPropertyBasedOnNameValue(
        propertyName,
        propertyValue,
        classes
      );
    }

    if (typeof propertyValue === 'boolean') {
      return propertyValue.toString();
    }

    return propertyValue;
  };

  const sanitizePropertyName = (propertyName: string) => {
    return propertyName.indexOf(TAG_KEY) === -1 ? (
      propertyName
    ) : (
      <div className={classes.tagContainer}>
        <TagIcon className={classes.tag} /> {propertyName.replace(TAG_KEY, '')}
      </div>
    );
  };

  const resetPropertyValue = () => {
    return updateEntity?.(Date.now(), {
      [propertyName]: propertyData.originalValue,
    });
  };

  const handleAction = (onClick: any) => () => {
    onClick(propertyName, propertyData.value, entityType);
  };

  const name = sanitizePropertyName(propertyName);

  return (
    <div className={classes.row}>
      <Tooltip
        title={`Original value: ${propertyData.originalValue}`}
        disableFocusListener={!propertyData.overridden}
        disableHoverListener={!propertyData.overridden}
        disableTouchListener={!propertyData.overridden}
      >
        <Typography className={classes.propertyName}>
          {name}
          <div className={classes.chipContainer}>
            {propertyData.enriched && (
              <IndicatorChip
                label="Enriched"
                color="blue"
                className={classes.chip}
                size="md"
              />
            )}
            {propertyData.overridden && (
              <IndicatorChip
                label="Overridden"
                color="blue"
                className={classes.chip}
                size="md"
              />
            )}
          </div>
        </Typography>
      </Tooltip>
      {propertyActions.length > 0 && (
        <>
          <IconButton className={classes.moreButton} onClick={menu.setAnchorEl}>
            <MoreIcon />
          </IconButton>
          <Menu
            id={`${propertyName}-menu`}
            anchorEl={menu.anchorEl}
            open={Boolean(menu.anchorEl)}
            onClose={menu.clearAnchorEl}
          >
            {propertyActions.map((action, index) => (
              <MenuItem
                key={index}
                {...(action.props as any)}
                onClick={handleAction(action.props.onClick)}
              />
            ))}
            {(Boolean(propertyData.overridden) ||
              Boolean(propertyData.enriched)) &&
              updateEntity !== undefined && (
                <MenuItem
                  key={propertyActions.length}
                  onClick={resetPropertyValue}
                >
                  Reset Value
                </MenuItem>
              )}
          </Menu>
        </>
      )}
      <Typography variant="body2" className={classes.propertyValue}>
        {getFieldContent(propertyName, propertyData.value)}
      </Typography>
    </div>
  );
};

export default withStyles(styles)(EntityDrawerProperty);
